/**
 * Chromium browsers serialize a copied selection by inlining the *computed*
 * styles of the selected nodes into the `text/html` clipboard flavor. For a
 * chat answer that means the bubble's dark `background-color` and light text
 * color travel with the copied text, and Word renders them as paragraph
 * shading (WF-2609).
 *
 * We take over the `copy` event and write our own `text/html`: the same
 * semantic markup (headings, lists, tables, emphasis, links) with every
 * presentational attribute stripped, so the paste target styles it natively.
 */

/** Attributes that carry chat styling into the paste target. */
const PRESENTATION_ATTRIBUTES = ['style', 'class', 'bgcolor', 'background', 'color']

/** UI chrome that sits inside a message but isn't part of its content. */
const NON_CONTENT_SELECTOR = 'button, .MuiSvgIcon-root, .MuiAvatar-root'

/**
 * Inline formatting that `Range.cloneContents()` drops when the entire
 * selection sits inside one such element (selecting part of a bold run, say).
 * We re-wrap the clone in those ancestors so the emphasis survives.
 */
const INLINE_FORMATTING_TAGS = new Set(['A', 'B', 'CODE', 'DEL', 'EM', 'I', 'S', 'STRONG', 'SUB', 'SUP', 'U'])

/** The slice of a copy event we need, satisfied by both native and React events. */
export type CopyEventLike = {
  defaultPrevented: boolean
  clipboardData: DataTransfer | null
  preventDefault: () => void
}

export function buildSanitizedCopyHtml(ranges: Range[]): string {
  const holder = document.createElement('div')
  ranges.forEach(range => holder.appendChild(range.cloneContents()))

  reapplyInlineAncestors(holder, ranges[0])

  holder.querySelectorAll(NON_CONTENT_SELECTOR).forEach(element => element.remove())
  holder.querySelectorAll('*').forEach(element => {
    PRESENTATION_ATTRIBUTES.forEach(attribute => element.removeAttribute(attribute))
  })

  return holder.innerHTML
}

/**
 * `copy` handler for message content. Falls through to the browser default
 * whenever there is nothing meaningful to rewrite.
 */
export function handleSanitizedCopy(event: CopyEventLike): void {
  if (event.defaultPrevented || !event.clipboardData) {
    return
  }

  const selection = window.getSelection()
  if (!selection || selection.isCollapsed || selection.rangeCount === 0) {
    return
  }

  const text = selection.toString()
  if (!text) {
    return
  }

  const ranges: Range[] = []
  for (let i = 0; i < selection.rangeCount; i += 1) {
    ranges.push(selection.getRangeAt(i))
  }

  event.clipboardData.setData('text/plain', text)
  event.clipboardData.setData('text/html', buildSanitizedCopyHtml(ranges))
  event.preventDefault()
}

function reapplyInlineAncestors(holder: HTMLElement, range: Range | undefined) {
  if (!range) {
    return
  }

  let node: Node | null = range.commonAncestorContainer
  if (node.nodeType === Node.TEXT_NODE) {
    node = node.parentNode
  }

  while (node instanceof HTMLElement && INLINE_FORMATTING_TAGS.has(node.tagName)) {
    const wrapper = node.cloneNode(false) as HTMLElement
    wrapper.append(...Array.from(holder.childNodes))
    holder.appendChild(wrapper)
    node = node.parentNode
  }
}
